import { Component, OnInit, OnDestroy, OnChanges, ChangeDetectorRef, SimpleChanges, ViewChild } from '@angular/core';
import { Router, ActivatedRoute, Data } from "@angular/router";
import { Location } from "@angular/common";
import { FormGroup, AbstractControl } from "@angular/forms";
import { Observable, combineLatest, of, Subscription, forkJoin } from "rxjs";
import { tap, mergeMap, finalize } from "rxjs/operators";

import { SubHeaderService } from "../../../core/services/sub-header.service";
import { NoteService } from "../../../common/services/note.service";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { LayoutService } from "../../../layouts/services/layout.service";
import { SidePanelService } from "../../../common/services/side-panel.service";
import { FlowButtonOverrideService } from "../../services/flow-button-override.service";
import { FlowInstanceService } from "../../services/flow-instance.service";
import { FlowOnBehalfService } from "../../services/flow-on-behalf.service";
import { AuthService } from "../../../services/auth.service";
import { LoaderService } from "../../../common/services/loader.service";
import { RuleBuilderService } from "../../../builder/services/rule-builder.service";
import { RuleCompiler } from "../../services/rule-compiler.service";
import { ContextInteractionService } from "../../../form-controls/services/context-interaction.service";
import { DomainDefinitionService } from "../../../builder/services/domain-definition.service";
import { StateType, PageType, SummaryControlConfig, SectionConfig, ButtonPosition, EjectLetterContent, PaperDocumentData, PageView, SectionPageIndex, FlowIndex } from "../../../common/models/flow.dto";
import { FlowInstance, Question, QuestionKind, Page, SummaryQuestion, Section, Button } from "../../models/flow.model";
import { Note } from "../../../common/models/note.model";
import { SidePanel } from "../../../common/models/side-panel.model";
import StaticPromise from "../../../common/models/static-promise";
import { SubHeader } from "../../../core/models/sub-header.model";
import { FlowComponent } from "../flow/flow.component";
import { deepClone } from "../../../builder/models/editor.model";
import { DateService } from "../../../common/services/date.service";
import { ClarificationNotesComponent } from "./clarification-notes/clarification-notes.component";
import { SummaryComponent } from "../../../form-controls/components/summary/summary.component";
import { FlowHistoryService } from "../../services/flow-history.service";
import { ApplicationConfigService, ModuleConfig } from "../../../services/application-config.service";
import { SystemSettingsService } from "../../../admin/services/system-settings.service"; 
import { SystemSetting, SystemSettingSearchSpec, SettingNames } from "../../../admin/models/system-setting.model";


@Component({
    selector: 'on-behalf-flow',
    templateUrl: './on-behalf-flow.component.html',
    styleUrls: ['../flow/flow.component.scss'],
    providers: [FlowButtonOverrideService],
    standalone: false
})
export class OnBehalfFlowComponent extends FlowComponent implements OnInit, OnDestroy, OnChanges {
    clarificationNotes: Map<Question, Note[]> = new Map<Question, Note[]>();
    displayMode: DisplayMode = DisplayMode.Flow;
    @ViewChild(ClarificationNotesComponent) noteComponent: ClarificationNotesComponent;
    ejectConfirmationDialog: Dialog = new Dialog("Flow has been ejected.  Letter has been sent to the queue for mailing.", [new DialogButton("OK", true, () => this.routeHome())])
    ejectLetterSubscription: Subscription;
    ejectSummary: Question;
    ejectSummaryLoaded: boolean = false;
    ejectSummarySubscription: Subscription;
    errorDialog: Dialog = new Dialog("An error has occurred.", [new DialogButton("OK", false, () => this.onDialogClose())]);
    letterContent: string = "";
    letterLoadError: string = "";    
    loadingContent: boolean = true;
    loadingPdf: boolean = true;
    modes = DisplayMode;
    notes: PageView[];
    notImplementedDialog: Dialog = new Dialog("This function has not yet been implemented.", [new DialogButton("Sorry!", true, () => this.onDialogClose())]);

    get isReadOnly(): boolean {
        return false;
    }

    onBehalfSidePanel: SidePanel = new SidePanel({
        tabs: [{ title: "Field Notes" }, { title: "Review Notes", stateTypeRequired: StateType.ReviewForm }, { title: "History", stateTypeRequired: StateType.ReviewForm }],
        sections: [{ componentName: "FieldNotes" }]
    });
    pdfData: any;
    pdfError: boolean = false;
    pdfUrl: string = "";
    summarySection: number;
    summaryPage: number;

    constructor(
        public router: Router,
        public route: ActivatedRoute,
        public location: Location,
        public subHeaderService: SubHeaderService,
        public noteService: NoteService,
        public dialogService: DynamicDialogService,
        public layoutService: LayoutService,
        public sidePanelService: SidePanelService,
        public flowOverrideService: FlowButtonOverrideService,
        public onBehalfService: FlowOnBehalfService,
        public readonly authService: AuthService,
        public readonly changeDetector: ChangeDetectorRef,
        public readonly loaderService: LoaderService,
        public readonly flowInstanceService: FlowInstanceService,
        public readonly ruleBuilder: RuleBuilderService,
        public readonly ruleCompiler: RuleCompiler,
        public readonly contextService: ContextInteractionService,
        public readonly dateService: DateService,
        public readonly domainService: DomainDefinitionService,
		historyService: FlowHistoryService,
		configService: ApplicationConfigService,
		settingsService: SystemSettingsService
    ) {
        super(router, route, location, subHeaderService, noteService, dialogService, layoutService, sidePanelService, flowOverrideService, authService, changeDetector,
            loaderService, flowInstanceService, ruleBuilder, ruleCompiler, historyService, configService, settingsService);
    }

    ngOnInit() {
        this.loaderService.toggleLoader(false);
        this.flowOverrideService.flow = this;
        this.infiniteLoopMessage = "Based on the recorded answers, the end of the flow is unreachable.  You may end up looping through the same page or sequence of pages multiple times.  Should that occur, using the Eject handle is advised.";
        const routeData$: Observable<Data> = this.route.data.pipe(
            tap((data: Data) => {
                if (!data.workflow) {
                    console.error("No data available for render; routing home");
                    this.routeHome();
                }
                if (typeof (data.workflow) === "string") {
                    this.forbid(data.workflow);
                } else {
                    let routeWorkflow: FlowInstance = data.workflow;
                    let routeWorkItemId = this.route.snapshot.params.workItemId;
                    if (!routeWorkflow.onBehalf) {
                        let reRoute: string = (routeWorkItemId == null)
                            ? `/flow/${routeWorkflow.id}`
                            : `/flow/${routeWorkflow.id}/${routeWorkItemId}`;
                        this.router.navigate([reRoute]);
                    }
                    if (!routeWorkflow.startAtSummary && (!data.workflow.context.navigations || data.workflow.context.navigations.length === 0)) {
                        this.returningUser = false;
                    }
                    this.loadWorkflow(routeWorkflow);
                }
            })
        );

        let indexObs$: Observable<FlowIndex> = of(null);

        if (this.route.snapshot.url[0].path !== "create") {
            let flowId = this.route.snapshot.params["id"];
            indexObs$ = (flowId != null && flowId.length > 0)
                ? this.flowInstanceService.findFlowIndex(flowId).pipe(
                    tap((idx: FlowIndex) => { this.currentFlowIndex = idx; })
                )
                : of(null);
        }

		indexObs$ = indexObs$.pipe(finalize(() => { this.indexSubject.next(true); }));

		let configObs$: Observable<any> = this._configService.getModuleConfig().pipe(
			tap((conf: ModuleConfig) => {
				this.moduleConfig = conf;
			}),
			mergeMap(() => {
				let spec: SystemSettingSearchSpec = {
					settingName: SettingNames.AllowExpedite
				}
				return this._settings.search(spec).pipe(
					tap((settings: SystemSetting[]) => {
						if (settings != null && settings.length > 0) {
							this.expediteSetting = settings.find(s => s.settingName === SettingNames.AllowExpedite);
						}
					})
				)
			})
		);

        const subs$ = forkJoin(routeData$, indexObs$, configObs$).subscribe(
            (data: any) => { },
            (error) => { console.error(error); }
        );

        this.subscriptions.add(subs$);

        let infinite$ = this.infiniteLoopDetected.asObservable().pipe()
            .subscribe(
                (isLooping) => {
                    this.infiniteLoop = isLooping;
                },
                (error) => { console.error(error); }
            );
        this.subscriptions.add(infinite$);

        let noteAdded$ = this.noteService.noteAdded$.asObservable().pipe(
            mergeMap((note) => {
                this.loaderService.toggleLoader(true);
                const obs$ = (this.workflow != null) ? this.saveNoteEvent() : of(note);
                return obs$;
            }))
            .subscribe((note) => {
                this.loaderService.toggleLoader(false);
                this.changeDetector.detectChanges();
            },
                (error) => {
                    console.error(error);
                    this.loaderService.toggleLoader(false);
                }
        );
        this.subscriptions.add(noteAdded$);
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        if (this.ejectSummarySubscription) this.ejectSummarySubscription.unsubscribe();
    }

    addFlowNote(): void {
        this.noteService.newNote({            
            data: this.contextService.getContextData("$.flowNotes", this.workflow.context),
            reviewNotes: this.contextService.getContextData("$.reviewNotes", this.workflow.context),
            instance: this.workflow,
            stateType: this.state.stateType,
            onBehalf: true
        });
        this.activeSidePanel = deepClone(this.onBehalfSidePanel);
        this.activeSidePanel.tabs = this.activeSidePanel.tabs.filter(t => !t.stateTypeRequired || t.stateTypeRequired === this.state.stateType);
        this.activeSidePanel.tabs = this.activeSidePanel.tabs.filter(t => t.title.toLocaleLowerCase() !== "field notes")
        this.sidePanelService.newSidePanel(this.activeSidePanel);
        this.sidePanelService.toggleSidePanel(true);
    }

    addQuestionNote(contextTarget: string): Observable<boolean> {
        if (!contextTarget) {
            console.warn(`Missing Context Target Configuration.  Cannot create a note.`);
            return;
        }

        let obs = this.noteService.newNote({
            data: this.contextService.getContextData(contextTarget, this.workflow.context),
            reviewNotes: this.contextService.getContextData("$.reviewNotes", this.workflow.context),
            instance: this.workflow,
            stateType: this.state.stateType,
            onBehalf: true
        });
        this.resetSidePanels();
        this.sidePanelService.toggleSidePanel(true);
        return obs;
    }

    public buttonAction(button: Button): any {
        this.setContextEjected("");
        return super.buttonAction(button);
    }

    calculateRequiredSections(configSections: SectionConfig[]): Observable<SectionConfig[]> {
        const pages: Page[] = [];
        const nextButtons = [];
        let totalPages: number = 0;
        const sectionPageIndices: Map<string, SectionPageIndex> = new Map<string, SectionPageIndex>();

        for (let s = 0; s < this.state.sections.length; s++) {
            let index = { startPageIndex: totalPages, endPageIndex: totalPages + this.state.sections[s].pages.length - 1 } as SectionPageIndex;
            sectionPageIndices.set(this.state.sections[s].id, index);
            totalPages += this.state.sections[s].pages.length;
            for (let p = 0; p < this.state.sections[s].pages.length; p++) {
                pages.push(this.state.sections[s].pages[p]);
                const right = this.state.sections[s].pages[p].buttons.find(b => b.position === ButtonPosition.Right);
                if (right != null) {
                    nextButtons.push(right);
                }
            }
        }
        const buttonPromises = nextButtons.map(b => {
            if (b.targetPage) {
                return b.targetPage();
            } else {
                return StaticPromise.from<number>(() => -1);
            }
        });

        return combineLatest(...buttonPromises).pipe(mergeMap((destinations: number[]) => {
            let nextPageIndex = 0;
            let pathLog: number[] = [];
            let sections: SectionConfig[] = [];

            do {
                if (pathLog.indexOf(nextPageIndex) !== -1) {
                    break;
                }
                pathLog.push(nextPageIndex);                
                for (const [key, value] of sectionPageIndices.entries()) {
                    let found: boolean = false;
                    let index = value as SectionPageIndex;
                    if (nextPageIndex >= index.startPageIndex && nextPageIndex <= index.endPageIndex) {
                        found = true;
                        const originalSection: Section = this.state.sections.find(s => s.id === key);
                        const useSection: SectionConfig = configSections.find(s => s.sectionId === key);
                        let storedSectionIndex = sections.findIndex(s => s.sectionId === key);
                        let storedSection: SectionConfig;
                        if (storedSectionIndex !== -1) {
                            storedSection = sections[storedSectionIndex];
                        } else {
                            storedSection = deepClone(useSection);
                            storedSection.summaryItems = [];
                            storedSection.isRequired = () => { return true; };
                        }
                        let relativePageIndex = nextPageIndex - index.startPageIndex;
                        let page = originalSection.pages[relativePageIndex];
                        let questions: string[] = [];
                        for (let r = 0; r < page.questions.length; r++) {
                            let row = page.questions[r];
                            for (let q = 0; q < row.length; q++) {
                                questions.push(row[q].key);
                            }                            
                        }
                        let filteredItems = useSection.summaryItems.filter(i => questions.findIndex(q => q == i.questionKey) !== -1);
                        for (let f = 0; f < filteredItems.length; f++) {
                            if (filteredItems[f].enabledRule == null) {
                                let row = page.questions.find(r => r.find(q => q.key === filteredItems[f].questionKey));
                                let question = row.find(q => q.key === filteredItems[f].questionKey);
                                if (question.enabled != null) {
                                    filteredItems[f].enabledRule = question.enabled;
                                }
                            }
                        }
                        storedSection.summaryItems = storedSection.summaryItems.concat(filteredItems);;
                        if (storedSectionIndex === -1) {
                            sections.push(storedSection);
                        }
                    }
                    if (found) break;
                }
                nextPageIndex = destinations[nextPageIndex];
            } while (nextPageIndex > 0);
            return of(sections);
        }))
    }

    cancelEject(): void {
        if (this.ejectSummarySubscription) {
            console.log("unsubscribing from eject summary getter");
            this.ejectSummarySubscription.unsubscribe();
        }
        if (this.ejectLetterSubscription) {
            console.log("unsubscribing from eject letter getter");
            this.ejectLetterSubscription.unsubscribe();
        }
        this.displayMode = DisplayMode.Flow;
        this.loadingContent = true;
        this.loadingPdf = true;
        this.pdfData = {};
        this.pdfUrl = "";
        let letterForm = this.ngForm.get("letter");
        if (letterForm) {
            this.ngForm.removeControl("letter");
        }
        this.subHeaderService.updateSubHeader(this.setSubheader());        
    }

    canShowNote(question: Question): boolean {
        return (question.type !== QuestionKind.Label && question.contextTarget != null);
    }

    defineEjectSummary(): Observable<Question> {
        this.ejectSummaryLoaded = false;
        let summaryPage: Page;
        let summaryQuestion: Question;
        const summarySection = this.state.sections.find(s => s.pages.findIndex(p => p.pageType === PageType.Summary) !== -1);
        if (summarySection) {
            summaryPage = summarySection.pages.find(p => p.pageType === PageType.Summary);
        }
        if (summaryPage != null) {
            let summaryRow = summaryPage.questions.find(r => r.find(q => q.type === QuestionKind.Summary));
            summaryQuestion = summaryRow.find(r => r.type === QuestionKind.Summary);
        }
        let artificialRequired: boolean = summaryQuestion == null;
        if (artificialRequired) {
            summaryQuestion = this.fabricateEjectSummary();
        }
        let config = summaryQuestion.config as SummaryControlConfig;
        let sectionObs$ = (artificialRequired)
            ? of(config.configurations)
            : this.calculateRequiredSections(config.configurations as SectionConfig[]);

        return sectionObs$.pipe(mergeMap((sections) => {
                let question = deepClone(summaryQuestion);
                question.config = { isEjectSummary: true, isReviewForm: config.isReviewForm, requiredDomains: config.requiredDomains, configurations: sections, sections: [], contextValues: config.contextValues } as SummaryControlConfig;
                return of(question);
            }));
    }

    eject(): void {
        if (this.ejectSummarySubscription != null) this.ejectSummarySubscription.unsubscribe();
        this.loaderService.toggleLoader(true);
        this.ejectSummarySubscription = this.saveContext().pipe(
            mergeMap((ctx) => {
                return this.defineEjectSummary();
            })).subscribe((question) => {
                this.loaderService.toggleLoader(false);
                this.ejectSummary = question;
                this.displayMode = DisplayMode.Eject;
                let ejectSubheader = this.setSubheader();
                ejectSubheader.buttons = ejectSubheader.buttons.filter(b => b.name.toLocaleLowerCase() !== "eject");
                let cancelButton = { name: "Cancel Eject", method: () => this.cancelEject() };
                ejectSubheader.buttons.push(cancelButton);
                this.subHeaderService.updateSubHeader(ejectSubheader);
            }, (error) => {
                    this.loaderService.toggleLoader(false);
                    console.error("Error generating summary for eject");
                    console.error(error);
        });           
    }

    private fabricateEjectSummary(): SummaryQuestion {
        let autohideComponents: QuestionKind[] = [QuestionKind.PaymentCheckout, QuestionKind.PaymentResponse];
        let tmpComponent = new SummaryComponent(this.dateService, this.changeDetector, this.domainService, this.noteService, this.ruleCompiler, this.contextService);
        let config = {
            sections: this.state.sections,
            isReviewForm: this.state.stateType === StateType.ReviewForm,
            requiredDomains: [],
            contextValues: [],
            configurations: []
        } as SummaryControlConfig;
        config = tmpComponent.loadConfig(config);

        config.configurations.forEach(c => {
            c.summaryItems.forEach(s => {
                s.alwaysShowInSummaries = autohideComponents.findIndex(q => q === s.type) === -1;
                s.isDisplayed = (s.contextValue !== "$.paperApplication" && s.alwaysShowInSummaries);
            });
        });

        let args = {
            config: config,
            context: this.workflow.context,
            contextTarget: "$.data.ejectSummary",
            initialDataContextTarget: "",
            enabled: (): boolean => { return true; },
            helpText: "",
            initialValue: () => { return "" },
            key: "ejectSummary",
            label: "",
            type: QuestionKind.Summary,
            pageValidationMessages: [],
            pageValidator: this.ruleCompiler.compileValidationRules([], this.workflow.context),
            flowValidationMessages: [],
            flowValidator: this.ruleCompiler.compileValidationRules([], this.workflow.context),
            visible: (): boolean => { return true; },
            changeRules: [],
            files: [],
            flexGrow: 1,
            defaultValue: ""
        };
        return Question.create(QuestionKind.Summary, args) as SummaryQuestion; 
    }

    generateLetter(): void {
        this.displayMode = DisplayMode.GenerateLetter;
        this.pinToTop();
        this.letterContent = "";
        this.pdfUrl = "";
        this.ejectLetterSubscription = this.onBehalfService.generateLetter(this.workflow.id, "Paper Submissions Eject Letter").pipe(tap((letter) => {
            this.letterContent = letter;
        })).subscribe(
            () => {
                console.log("letter content received");
                this.loadingContent = false;
            },
            (error) => {
                console.error(error);
                this.loadingContent = false;
                this.letterLoadError = "An error has occurred in loading the letter.  Please contact support.";
            }
        );
    }  

    private getPaperDocumentReferences(): PaperDocumentData[] {
        const urls: PaperDocumentData[] = [];
        const inputStates = this.workflow.states.filter(s => s.stateType === StateType.InputForm);
        let scanPage: Page = undefined;
        for (let s = 0; s < inputStates.length; s++) {
            const state = inputStates[s];
            const section = state.sections.find(st => st.pages.findIndex(p => p.pageType === PageType.PaperScanning) !== -1);
            if (section != null) {
                scanPage = section.pages.find(p => p.pageType === PageType.PaperScanning);
            }
            if (scanPage != null) break;
        }
        if (scanPage != null) {
            for (let r = 0; r < scanPage.questions.length; r++) {
                const row = scanPage.questions[r];
                for (let q = 0; q < row.length; q++) {
                    const question = row[q];
                    if (question.type === QuestionKind.FileUpload) {
                        let data = this.contextService.getContextData(`${question.contextTarget}.value`, this.workflow.context);
                        if (data instanceof Array) {
                            (data as any[]).forEach(d => {
                                if (d.blobUri && d.type) {
                                    urls.push({ contentType: d.type, blobUri: d.blobUri } as PaperDocumentData);
                                }
                            })
                        } else if (data.blobUri && data.type) {
                            urls.push({ contentType: data.type, blobUri: data.blobUri } as PaperDocumentData);
                        }
                    }
                }
            }
        }
       
        return urls;
    }

    loadWorkflow(routeWorkflow: FlowInstance): void {
        this.workflow = routeWorkflow;
        this.state = this.workflow.findCurrentState();
        this.summarySection = this.state.sections.findIndex(s => s.pages.findIndex(p => p.pageType === PageType.Summary) !== -1);
        if (this.summarySection !== -1) {
            this.summaryPage = this.state.sections[this.summarySection].pages.findIndex(p => p.pageType === PageType.Summary);
        }
        this.enableNavigationTracking = this.state.stateType === StateType.InputForm;
        if (this.enableNavigationTracking) {
            const navigationSubscription = this._navigation.pipe(tap((data) => {
                if (this.returningUser) {
                    this.returningUser = false;
                } else {
                    this.workflow.context.navigations.push(data);
                }
            })).subscribe(
                (data) => {
                    console.log(`navigation from Section ${data.section} Page ${data.page}`);
                },
                (error) => {
                    console.error(error);
                }
            );
            this.subscriptions.add(navigationSubscription);
        }
        this.resetSidePanels();
        

        this.goToInitialPage();
        this.layoutService.update("FlowComponent");
        
        this.subHeaderService.updateSubHeader(this.setSubheader());
    }

    navigateSection(section: Section): void {
        if (this.displayMode !== DisplayMode.Flow) {
            this.cancelEject();
        }
        super.navigateSection(section);
    }

    onSummaryLoaded(isLoaded: boolean): void {
        this.ejectSummaryLoaded = isLoaded;
    }

    onTextFormBuilt(form: FormGroup): void {
        this.ngForm.addControl("letter", form);       
    }

    previewLetter(): void { 
        this.loadingPdf = true;
        this.pdfError = false;
        this.displayMode = DisplayMode.PreviewLetter;
        const editCtrl = this.ngForm.get("letter");
        const contentCtrl = (editCtrl as FormGroup).get("content");
        let documentationUrls = this.getPaperDocumentReferences();
        let content: EjectLetterContent = {
            documents: documentationUrls,
            organizationId: this.workflow.organizationId,
            organizationName: "",
            text: contentCtrl.value,
            notes: [],
            workflowId: this.workflow.id
        } as EjectLetterContent;
        let letterSubs$ = this.onBehalfService.generatePDF(content).pipe((tap((data) => {
            this.pdfUrl = window.URL.createObjectURL(data);
        }))).subscribe(
            (data) => {
                this.pdfData = data;
                this.loadingPdf = false;
            },
            (error) => {
                console.error(error);
                this.loadingPdf = false;
                this.pdfError = true;
                this.showError();
            });
        this.subscriptions.add(letterSubs$);
        
    }

    resetSidePanels(): void {
        super.resetSidePanels();
        this.onBehalfSidePanel = new SidePanel({
            tabs: [{ title: "Field Notes" }, { title: "Review Notes", stateTypeRequired: StateType.ReviewForm }, { title: "History", stateTypeRequired: StateType.ReviewForm }],
            sections: [{ componentName: "FieldNotes" }, { componentName: "ReviewNotes" }, { componentName: "History" }]
        });
        this.activeSidePanel = deepClone(this.onBehalfSidePanel);
        this.activeSidePanel.tabs = this.activeSidePanel.tabs.filter(t => !t.stateTypeRequired || t.stateTypeRequired === this.state.stateType);
        this.sidePanelService.newSidePanel(this.activeSidePanel);
    }

    reviewSummary(): void {
        if (this.ejectLetterSubscription) this.ejectLetterSubscription.unsubscribe();
        this.eject();
    }

    routeHome(): void {
        this.router.navigate(["/home"]);
    }

    setContextEjected(ejected: Date | string): void {
        this.contextService.setContextData("$.ejected", ejected, this.workflow.context);
    }

    setSubheader(): SubHeader {
        const subheader = new SubHeader({ title: this.title, buttons: [] });
        if (this.workflow.currentWorkItem != null) {
            const buttonName = this.state.stateType === StateType.InputForm ? "View Notes" : "View Notes & Timeline";
            subheader.buttons.push({ name: buttonName, method: () => this.addFlowNote() });
        }
        if (this.displayMode != DisplayMode.Eject) {
            let summaryRow = this.page.questions.find(r => r.find(q => q.type === QuestionKind.Summary));
            if (summaryRow != null) {
                let summaryQuestion: Question = summaryRow.find(r => r.type === QuestionKind.Summary);
                if (summaryQuestion != null) {
                    subheader.buttons.push(this.printPdfButton);
                }
            }
        }    
        const ejectButton = { name: "Eject", method: () => this.eject() };
        subheader.buttons.push(ejectButton);
        return subheader;
    }



    showError(): void {
        this.errorDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.errorDialog);
    }

    submitLetter(): void {
        const editCtrl = this.ngForm.get("letter");
        const contentCtrl = (editCtrl as FormGroup).get("content");
        let documentationUrls = this.getPaperDocumentReferences();
        let content: EjectLetterContent = {
            documents: documentationUrls,
            organizationId: this.workflow.organizationId,
            organizationName: "",
            text: contentCtrl.value,
            notes: this.notes,
            workflowId: this.workflow.id
        } as EjectLetterContent;
        this.loaderService.toggleLoader(true);
        const queueSubs$ = this.onBehalfService.queueLetter(content).pipe().subscribe(
            () => {
                this.loaderService.toggleLoader(false);
                console.log("letter submitted and flow ejected");
                this.ejectConfirmationDialog.toggleDialog(true);
                this.dialogService.updateDialog(this.ejectConfirmationDialog);
            },
            (error) => {
                console.error(error);
                this.loaderService.toggleLoader(false);
                this.showError();
            }
        );
        this.subscriptions.add(queueSubs$);
    }
}


enum DisplayMode {
    Flow,
    Eject,
    GenerateLetter,
    PreviewLetter
}